/**
 * Environment Debugging Utility
 *
 * Provides debugging information about environment variables and configuration.
 * Only logs in development mode.
 */

import { Platform } from 'react-native';
import { getApiConfig, isWebpackDevLocalWebOrigin, logApiConfig } from '../config/api';
import { getEnvironmentInfo } from '../config/env';

/**
 * Log comprehensive environment information to console
 * This helps debug configuration issues during development
 */
export function logEnvironmentInfo(): void {
  if (!__DEV__) {
    return; // Only log in development mode
  }

  console.group('🔍 Environment Configuration Debug');

  // Platform info
  console.log('%c📱 Platform Information', 'font-weight: bold; color: #2196F3');
  console.log(`  Platform: ${Platform.OS}`);
  console.log(`  Version: ${Platform.Version}`);

  // API Configuration
  console.log('%c🔧 API Configuration', 'font-weight: bold; color: #4CAF50');
  logApiConfig();

  // All environment variables
  console.log('%c📋 Environment Variables', 'font-weight: bold; color: #FF9800');
  const envVars = getEnvironmentInfo();
  console.table(envVars);

  // Validation status
  console.log('%c✅ Configuration Status', 'font-weight: bold; color: #8BC34A');
  const config = getApiConfig();
  const webUsesWebpackProxy =
    Platform.OS === 'web' && isWebpackDevLocalWebOrigin() && !config.BASE_URL;
  console.log(
    `  API Base URL: ${
      config.BASE_URL
        ? '✅ SET'
        : webUsesWebpackProxy
          ? '✅ same-origin /api (webpack dev; empty BASE_URL is intentional)'
          : '❌ MISSING'
    }`,
  );
  console.log(`  Web Base URL: ${config.WEB_BASE_URL ? '✅ SET' : '❌ MISSING'}`);
  console.log(`  API Token: ${config.API_TOKEN ? '✅ SET' : 'ℹ️  Not set (optional)'}`);

  // Build info
  console.log('%c🏗️  Build Information', 'font-weight: bold; color: #9C27B0');
  console.log(`  Build Time: ${new Date().toISOString()}`);
  console.log(`  Development Mode: ${__DEV__ ? 'Yes' : 'No'}`);
  console.log(`  Node Environment: ${process.env.NODE_ENV || 'not set'}`);

  console.groupEnd();
}

/**
 * Log a specific API request for debugging
 */
export function logApiRequest(method: string, url: string, options?: RequestInit): void {
  if (!__DEV__) {
    return;
  }

  console.log(`🌐 API Request: ${method} ${url}`, options || {});
}

/**
 * Log environment variable issues
 */
export function logEnvWarning(message: string): void {
  console.warn(`⚠️ Environment Config Warning: ${message}`);
}

/**
 * Log environment variable errors
 */
export function logEnvError(message: string): void {
  console.error(`❌ Environment Config Error: ${message}`);
}
